import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type IconBadgeTone =
  | 'water'
  | 'forest'
  | 'cyan'
  | 'rose'
  | 'amber'
  | 'locked';

export type IconBadgeSize = 'sm' | 'md' | 'lg';

export interface IconBadgeProps {
  children: ReactNode;
  className?: string;
  size?: IconBadgeSize;
  /** Color family — pairs with the parent card tone. Defaults to water. */
  tone?: IconBadgeTone;
}

const SIZE_MAP: Record<IconBadgeSize, string> = {
  sm: 'h-10 w-10',
  md: 'h-12 w-12',
  lg: 'h-14 w-14',
};

const TONE_MAP: Record<IconBadgeTone, string> = {
  water: 'bg-water-50 ring-1 ring-inset ring-[#CFE3F3] text-water-700',
  forest: 'bg-forest-50 ring-1 ring-inset ring-[#CFE6D8] text-forest-700',
  cyan: 'bg-cyan2-50 ring-1 ring-inset ring-[#C8EAF1] text-cyan2-600',
  rose: 'bg-rose2-50 ring-1 ring-inset ring-[#F4C6DB] text-rose2-600',
  amber: 'bg-amber2-50 ring-1 ring-inset ring-[#F4DDB7] text-amber2-600',
  locked: 'bg-paper-warm ring-1 ring-inset ring-line text-slate2-400',
};

/**
 * Square-rounded icon container used as the visual anchor on quest cards.
 * Tone is set by the parent (matches its `.bw-card-*` variant).
 */
export function IconBadge({
  children,
  className,
  size = 'md',
  tone = 'water',
}: IconBadgeProps) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-2xl',
        SIZE_MAP[size],
        TONE_MAP[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export default IconBadge;
